import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Loader2, Plus, Search, Eye, Trash2, ArrowRightLeft, Upload, Truck, CheckCircle, Package, FileText, Clock, AlertTriangle, X, Download, Printer, Check, Edit3, ExternalLink } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { stockTransferAPI, branchAPI, productAPI, fileAPI } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import { toast } from 'sonner';
import ProductSearchInput from '../ui/ProductSearchInput';
import Pagination from '../ui/Pagination';
import jsPDF from 'jspdf';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  Draft: 'bg-gray-100 text-gray-600',
  Confirmed: 'bg-green-100 text-green-700',
  'Ready to Dispatch': 'bg-amber-100 text-amber-700',
  Dispatched: 'bg-blue-100 text-blue-700',
  Received: 'bg-emerald-100 text-emerald-700',
};

const StockTransferModule = () => {
  const location = useLocation();
  const [view, setView] = useState('list');
  const [stns, setStns] = useState([]);
  const [stats, setStats] = useState({});
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedSTN, setSelectedSTN] = useState(null);

  // Deep-link: open STN detail from URL params (?stn=id)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stnId = params.get('stn');
    if (stnId && view === 'list') {
      // STNDetailView expects an ID string and re-fetches itself.
      // (Table click flow uses setSelectedSTN(stn.id) — match that.)
      setSelectedSTN(stnId);
      setView('detail');
    }
  }, [location.search]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: 20 };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const [result, st, br] = await Promise.all([
        stockTransferAPI.getAll(params),
        stockTransferAPI.getStats(),
        branchAPI.getAll(),
      ]);
      setStns(result.items || []);
      setTotalPages(result.total_pages || 1);
      setTotalItems(result.total || 0);
      setStats(st);
      setBranches(br);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Quick actions from list
  const handleConfirmFromList = async (stn) => {
    if (!window.confirm(`Confirm STN ${stn.stn_number}? This will check stock and create production orders for any shortfall.`)) return;
    try {
      const result = await stockTransferAPI.confirm(stn.id);
      if (result.shortfall_items?.length > 0) {
        toast.success(`${stn.stn_number} confirmed. ${result.production_orders_created} production order(s) created for shortfall.`, { duration: 5000 });
      } else {
        toast.success(`${stn.stn_number} confirmed. All stock available.`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    }
  };

  const downloadSTNPdf = (stn) => {
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('STOCK TRANSFER NOTE', 105, 20, { align: 'center' });
    doc.setFontSize(9);
    doc.text('SHARDA INFRASOLUTIONS PVT. LTD.', 105, 28, { align: 'center' });
    doc.setDrawColor(36, 84, 156);
    doc.setLineWidth(0.5);
    doc.line(15, 32, 195, 32);

    let y = 42;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const addRow = (label, value) => { doc.setFont('helvetica', 'bold'); doc.text(label, 15, y); doc.setFont('helvetica', 'normal'); doc.text(String(value || '-'), 70, y); y += 6; };

    addRow('STN #:', stn.stn_number);
    addRow('Status:', stn.status);
    addRow('Date:', stn.created_at ? new Date(stn.created_at).toLocaleDateString('en-IN') : '-');
    addRow('From:', stn.source_branch_name);
    addRow('To:', stn.destination_branch_name);
    if (stn.ship_to) addRow('Ship To:', stn.ship_to);
    if (stn.vehicle_no) addRow('Vehicle:', stn.vehicle_no);
    if (stn.transporter_name) addRow('Transporter:', stn.transporter_name);
    if (stn.lr_number) addRow('LR Number:', stn.lr_number);
    if (stn.invoice_number) addRow('Invoice #:', stn.invoice_number);
    if (stn.invoice_date) addRow('Invoice Date:', stn.invoice_date);

    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('ITEMS', 15, y); y += 4;
    doc.line(15, y, 195, y); y += 5;

    doc.setFontSize(8);
    doc.text('#', 18, y); doc.text('Product', 30, y); doc.text('Qty', 110, y); doc.text('Rate', 130, y); doc.text('Amount', 155, y); doc.text('Total', 178, y);
    y += 2; doc.line(15, y, 195, y); y += 5;

    doc.setFont('helvetica', 'normal');
    (stn.items || []).forEach((item, i) => {
      doc.text(String(i + 1), 18, y);
      doc.text(item.product_name || '', 30, y);
      doc.text(`${item.quantity} ${item.unit}`, 110, y);
      doc.text(String(item.rate || 0), 130, y);
      doc.text(String(item.amount || 0), 155, y);
      doc.text(String(item.total_amount || 0), 178, y);
      y += 5;
    });

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.text(`Subtotal: INR ${(stn.subtotal || 0).toLocaleString()}`, 140, y); y += 5;
    doc.text(`GST: INR ${(stn.total_gst || 0).toLocaleString()}`, 140, y); y += 5;
    doc.text(`Grand Total: INR ${(stn.grand_total || 0).toLocaleString()}`, 140, y);

    if (stn.remarks) { y += 8; doc.setFont('helvetica', 'bold'); doc.text('Remarks:', 15, y); y += 5; doc.setFont('helvetica', 'normal'); doc.text(stn.remarks, 15, y); }

    doc.save(`${stn.stn_number}.pdf`);
  };

  if (view === 'create') {
    return <STNCreateForm branches={branches} onBack={() => { setView('list'); fetchData(); }} />;
  }

  if (view === 'detail' && selectedSTN) {
    return <STNDetailView stnId={selectedSTN} branches={branches} onBack={() => { setView('list'); setSelectedSTN(null); fetchData(); }} />;
  }

  return (
    <div data-testid="stock-transfer-module">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Stock Transfers</h2>
          <p className="text-[11px] text-gray-500">{totalItems} total transfers</p>
        </div>
        <button data-testid="stn-create-btn" onClick={() => setView('create')}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> New Transfer
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
        {[
          { label: 'Draft', val: stats.draft || 0, color: 'text-gray-600' },
          { label: 'Confirmed', val: stats.confirmed || 0, color: 'text-green-600' },
          { label: 'Ready', val: stats.ready_to_dispatch || 0, color: 'text-amber-600' },
          { label: 'Dispatched', val: stats.dispatched || 0, color: 'text-blue-600' },
          { label: 'Received', val: stats.received || 0, color: 'text-emerald-600' },
          { label: 'Total', val: stats.total || 0, color: 'text-gray-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-3 text-center" data-testid={`stn-stat-${s.label.toLowerCase()}`}>
            <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
            <p className="text-[10px] text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="stn-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by STN#, branch..."
            className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 overflow-x-auto">
          {['all', 'Draft', 'Confirmed', 'Ready to Dispatch', 'Dispatched', 'Received'].map(s => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
              data-testid={`stn-filter-${s.replace(/\s/g, '-').toLowerCase()}`}
              className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-colors whitespace-nowrap ${statusFilter === s ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : stns.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <ArrowRightLeft className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-400">No stock transfers found</p>
          <p className="text-xs text-gray-400 mt-1">Create a new transfer to move goods between branches</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 font-semibold text-gray-500">STN #</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">From</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">To</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Items</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-500">Amount</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Date</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stns.map(stn => (
                <tr key={stn.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => { setSelectedSTN(stn.id); setView('detail'); }}>
                  <td className="px-4 py-3 font-semibold text-blue-700" data-testid={`stn-row-${stn.stn_number}`}>{stn.stn_number}</td>
                  <td className="px-4 py-3 text-gray-700">{stn.source_branch_name}</td>
                  <td className="px-4 py-3 text-gray-700">{stn.destination_branch_name}</td>
                  <td className="px-4 py-3 text-gray-500">{(stn.items || []).length} items</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-800">{stn.grand_total ? `INR ${stn.grand_total.toLocaleString('en-IN')}` : '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColors[stn.status] || 'bg-gray-100 text-gray-600'}`}>{stn.status}</span>
                    {stn.invoice_number && (
                      <span className="ml-1 text-[9px] text-emerald-600 font-mono">#{stn.invoice_number}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{stn.created_at ? new Date(stn.created_at).toLocaleDateString('en-IN') : '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button data-testid={`stn-view-${stn.id}`} onClick={() => { setSelectedSTN(stn.id); setView('detail'); }}
                        className="p-1 hover:bg-blue-50 rounded-md text-blue-600" title="View"><Eye className="w-3.5 h-3.5" /></button>
                      <button onClick={() => downloadSTNPdf(stn)} className="p-1 rounded hover:bg-gray-100" title="Download"><Download className="w-3.5 h-3.5 text-gray-500" /></button>
                      <button onClick={() => { downloadSTNPdf(stn); window.print(); }} className="p-1 rounded hover:bg-gray-100" title="Print"><Printer className="w-3.5 h-3.5 text-gray-500" /></button>
                      {stn.status === 'Draft' && (
                        <button data-testid={`stn-confirm-${stn.id}`} onClick={() => handleConfirmFromList(stn)}
                          className="p-1 rounded hover:bg-green-50" title="Confirm STN"><Check className="w-3.5 h-3.5 text-green-600" /></button>
                      )}
                      {stn.status === 'Confirmed' && (
                        <button data-testid={`stn-dispatch-${stn.id}`} onClick={() => { setSelectedSTN(stn.id); setView('detail'); }}
                          className="p-1 rounded hover:bg-amber-50" title="Ready to Dispatch"><Truck className="w-3.5 h-3.5 text-amber-600" /></button>
                      )}
                      {stn.status === 'Ready to Dispatch' && !stn.invoice_number && (
                        <button data-testid={`stn-invoice-${stn.id}`} onClick={() => { setSelectedSTN(stn.id); setView('detail'); }}
                          className="p-1 rounded hover:bg-blue-50" title="Add Invoice"><FileText className="w-3.5 h-3.5 text-blue-600" /></button>
                      )}
                      {stn.status === 'Ready to Dispatch' && stn.invoice_number && (
                        <button data-testid={`stn-confirm-dispatch-${stn.id}`} onClick={() => { setSelectedSTN(stn.id); setView('detail'); }}
                          className="p-1 rounded hover:bg-emerald-50" title="Confirm Dispatch"><Check className="w-3.5 h-3.5 text-emerald-600" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="p-3 border-t border-gray-100">
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== CREATE FORM ====================

const STNCreateForm = ({ branches, onBack }) => {
  const [sourceBranch, setSourceBranch] = useState('');
  const [destBranch, setDestBranch] = useState('');
  const [shipTo, setShipTo] = useState('');
  const [shipToSameAsBranch, setShipToSameAsBranch] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard
  const [stockMap, setStockMap] = useState({});

  // "Add Signage" modal — define a brand-new signage board (product + BOM) to transfer/produce
  const [showSignageModal, setShowSignageModal] = useState(false);
  const [savingSignage, setSavingSignage] = useState(false);
  const emptySignage = { shape: 'Rectangular', length_mm: '', height_mm: '', diameter_mm: '', dimensions: '', base_material: '', description: '', quantity: 1 };
  const [signageForm, setSignageForm] = useState(emptySignage);
  const roundShapes = ['Circular', 'Octagon', 'Round'];
  const lhShapes = ['Rectangular', 'Gantry', 'Hazard'];

  const handleAddSignage = async () => {
    const sh = signageForm.shape;
    const hasLH = lhShapes.includes(sh) && Number(signageForm.length_mm) > 0 && Number(signageForm.height_mm) > 0;
    const hasDia = Number(signageForm.diameter_mm) > 0;
    const hasDims = (signageForm.dimensions || '').trim().length > 0;
    if (!sh) { toast.error('Select a shape'); return; }
    if (!hasLH && !hasDia && !hasDims) { toast.error('Enter dimensions (Length x Height, Diameter, or a Dimensions value)'); return; }
    const qty = Number(signageForm.quantity) || 0;
    if (qty <= 0) { toast.error('Quantity must be greater than 0'); return; }
    try {
      setSavingSignage(true);
      const product = await stockTransferAPI.createSignageProduct({
        shape: sh,
        length_mm: Number(signageForm.length_mm) || 0,
        height_mm: Number(signageForm.height_mm) || 0,
        diameter_mm: Number(signageForm.diameter_mm) || 0,
        dimensions: (signageForm.dimensions || '').trim(),
        base_material: (signageForm.base_material || '').trim(),
        description: (signageForm.description || '').trim(),
      });
      if (items.some(i => i.product_id === product.id)) {
        toast.error('This signage is already added');
      } else {
        setItems(prev => [...prev, {
          product_id: product.id,
          product_name: product.name,
          sku: product.sku || '',
          hsn_code: product.hsn_code || '',
          quantity: qty,
          unit: product.unit || 'PCS',
          rate: product.selling_price || 0,
          gst_percent: product.gst_rate || 18,
        }]);
        toast.success(product.existing ? `Added existing signage: ${product.name}` : `New signage created: ${product.name}`);
      }
      setShowSignageModal(false);
      setSignageForm(emptySignage);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add signage');
    } finally {
      setSavingSignage(false);
    }
  };

  useEffect(() => {
    productAPI.getAll({ page_size: 2000 }).then(data => {
      setProducts(Array.isArray(data) ? data : (data?.items || []));
    }).catch(() => {});
  }, []);

  // Fetch current stock at source branch whenever source branch or items change
  useEffect(() => {
    if (!sourceBranch || items.length === 0) { setStockMap({}); return; }
    const productIds = items.map(i => i.product_id).filter(Boolean);
    if (productIds.length === 0) return;
    import('../../services/api').then(({ stockAPI }) => {
      stockAPI.batchNetQty(sourceBranch, productIds).then(data => {
        setStockMap(data?.stock || {});
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceBranch, items.length, items.map(i => i.product_id).join(',')]);

  const dstBranchObj = branches.find(b => b.id === destBranch);
  useEffect(() => {
    if (shipToSameAsBranch && dstBranchObj) {
      const parts = [dstBranchObj.address, dstBranchObj.city, dstBranchObj.state, dstBranchObj.pincode].filter(Boolean);
      setShipTo(parts.join(', '));
    }
  }, [shipToSameAsBranch, dstBranchObj]);

  const addItem = (product) => {
    if (!product) return;
    if (items.some(i => i.product_id === product.id)) {
      toast.error('Item already added');
      return;
    }
    setItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku || '',
      hsn_code: product.hsn_code || '',
      quantity: 1,
      unit: product.unit || 'PCS',
      rate: product.selling_price || 0,
      gst_percent: product.gst_rate || 18,
    }]);
  };

  const addEmptyRow = () => {
    setItems(prev => [...prev, {
      product_id: '',
      product_name: '',
      sku: '',
      hsn_code: '',
      quantity: 1,
      unit: 'PCS',
      rate: 0,
      gst_percent: 18,
    }]);
  };

  const fillRowFromProduct = (idx, product) => {
    if (items.some((item, i) => i !== idx && item.product_id === product.id)) {
      toast.error('Item already added');
      return;
    }
    setItems(prev => prev.map((item, i) => i === idx ? {
      ...item,
      product_id: product.id,
      product_name: product.name,
      sku: product.sku || '',
      hsn_code: product.hsn_code || '',
      unit: product.unit || 'PCS',
      rate: product.selling_price || 0,
      gst_percent: product.gst_rate || 18,
    } : item));
  };

  const clearRowProduct = (idx) => {
    setItems(prev => prev.map((item, i) => i === idx ? {
      ...item,
      product_id: '',
      product_name: '',
      sku: '',
      hsn_code: '',
      rate: 0,
    } : item));
  };

  const handleSTNProductSelect = (idx, product) => {
    fillRowFromProduct(idx, product);
  };

  const updateRowField = (idx, field, val) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: parseFloat(val) || 0 } : item));

  const srcBranch = branches.find(b => b.id === sourceBranch);

  const calcAmount = (item) => round2(item.quantity * item.rate);
  const calcGst = (item) => round2(calcAmount(item) * (item.gst_percent / 100));
  const calcTotal = (item) => round2(calcAmount(item) + calcGst(item));
  const subtotal = items.reduce((sum, item) => sum + calcAmount(item), 0);
  const totalGst = items.reduce((sum, item) => sum + calcGst(item), 0);
  const grandTotal = round2(subtotal + totalGst);

  const handleSubmit = async () => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicates
    if (!sourceBranch || !destBranch) { toast.error('Select source and destination branches'); return; }
    if (sourceBranch === destBranch) { toast.error('Source and destination must be different'); return; }
    const validItems = items.filter(i => i.product_id);
    if (validItems.length === 0) { toast.error('Add at least one item'); return; }
    if (validItems.some(i => i.quantity <= 0)) { toast.error('All quantities must be greater than 0'); return; }

    submittingRef.current = true;
    try {
      setSubmitting(true);
      const payload = {
        source_branch: sourceBranch,
        source_branch_name: srcBranch?.name || '',
        destination_branch: destBranch,
        destination_branch_name: dstBranchObj?.name || '',
        ship_to: shipTo,
        ship_to_same_as_branch: shipToSameAsBranch,
        items: items.filter(i => i.product_id).map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          sku: i.sku,
          hsn_code: i.hsn_code,
          quantity: i.quantity,
          unit: i.unit,
          rate: i.rate,
          gst_percent: i.gst_percent,
        })),
        remarks,
        subtotal,
        total_gst: totalGst,
        grand_total: grandTotal,
      };

      const stn = await stockTransferAPI.create(payload);
      toast.success(`${stn.stn_number} saved as Draft.`);
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create transfer');
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  return (
    <div data-testid="stn-create-form">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-4" data-testid="stn-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Stock Transfers
      </button>
      <h2 className="text-base font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>New Stock Transfer Note</h2>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        {/* Branch Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Source Branch *</label>
            <select data-testid="stn-source-branch" value={sourceBranch} onChange={e => setSourceBranch(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
              <option value="">Select source branch</option>
              {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name} ({b.type})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Destination Branch *</label>
            <select data-testid="stn-dest-branch" value={destBranch} onChange={e => { setDestBranch(e.target.value); if (shipToSameAsBranch) setShipToSameAsBranch(false); }}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
              <option value="">Select destination branch</option>
              {filterBranchesForUser(branches).filter(b => b.id !== sourceBranch).map(b => <option key={b.id} value={b.id}>{b.name} ({b.type})</option>)}
            </select>
          </div>
        </div>

        {/* Ship To */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="text-[11px] font-semibold text-gray-600">Ship To Address</label>
            {destBranch && (
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={shipToSameAsBranch} data-testid="stn-ship-to-same-checkbox"
                  onChange={e => { setShipToSameAsBranch(e.target.checked); if (!e.target.checked) setShipTo(''); }}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-200" />
                <span className="text-[10px] text-gray-500">Same as destination branch address</span>
              </label>
            )}
          </div>
          <textarea data-testid="stn-ship-to" value={shipTo} onChange={e => { if (!shipToSameAsBranch) setShipTo(e.target.value); }}
            readOnly={shipToSameAsBranch}
            placeholder="Enter shipping address for destination branch..."
            className={`w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 min-h-[60px] ${shipToSameAsBranch ? 'bg-gray-50 text-gray-500' : ''}`} />
        </div>

        {/* Items Table */}
        <div>
          <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Items *</label>

          <div className="border border-gray-100 rounded-xl" style={{ overflow: 'visible' }}>
            <div style={{ overflow: 'visible' }}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 w-[140px]">Item ID</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500">Item</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 w-[100px]">HSN</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 w-[80px]">Avail Stock</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 w-[90px]">Quantity</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 w-[110px]">Unit Price</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 w-[120px]">Total Amount</th>
                    <th className="px-2 py-2 w-[36px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-gray-400 text-xs">Click "+ Add Row" to add items</td>
                    </tr>
                  ) : items.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-50">
                      {/* Item ID - Searchable by SKU */}
                      <td className="px-2 py-1.5 relative">
                        <input
                          data-testid={`stn-item-id-${idx}`}
                          value={item._skuSearch !== undefined ? item._skuSearch : (item.sku || '')}
                          onChange={e => {
                            const val = e.target.value;
                            setItems(prev => prev.map((it, i) => i === idx ? { ...it, _skuSearch: val, _showSkuDrop: true } : it));
                            if (!val) clearRowProduct(idx);
                          }}
                          onFocus={() => {
                            setItems(prev => prev.map((it, i) => i === idx ? { ...it, _skuSearch: it.sku || '', _showSkuDrop: true } : it));
                          }}
                          onBlur={() => setTimeout(() => {
                            setItems(prev => prev.map((it, i) => i === idx ? { ...it, _skuSearch: undefined, _showSkuDrop: false } : it));
                          }, 200)}
                          placeholder="Search SKU..."
                          className="w-full px-2 py-1.5 text-xs font-mono border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200"
                        />
                        {item._showSkuDrop && (item._skuSearch || '').length > 0 && (
                          <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-40 overflow-y-auto">
                            {products
                              .filter(p => p.sku?.toLowerCase().includes((item._skuSearch || '').toLowerCase()))
                              .slice(0, 8)
                              .map(p => (
                                <div key={p.id}
                                  onMouseDown={() => fillRowFromProduct(idx, p)}
                                  className="px-2 py-1.5 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                                  <span className="font-mono font-semibold text-blue-700">{p.sku}</span>
                                  <span className="text-gray-400 ml-1.5">{p.name}</span>
                                </div>
                              ))}
                            {products.filter(p => p.sku?.toLowerCase().includes((item._skuSearch || '').toLowerCase())).length === 0 && (
                              <div className="px-2 py-2 text-xs text-gray-400 text-center">No match</div>
                            )}
                          </div>
                        )}
                      </td>
                      {/* Item Name - Searchable via ProductSearchInput */}
                      <td className="px-2 py-1.5" style={{ overflow: 'visible' }}>
                        <ProductSearchInput
                          value={item.product_name || ''}
                          productId={item.product_id || ''}
                          onSelect={(p) => handleSTNProductSelect(idx, p)}
                          onChange={(text) => updateRowField(idx, 'product_name', text)}
                          onClear={() => clearRowProduct(idx)}
                          placeholder="Search product by name or SKU..."
                          testId={`stn-item-name-${idx}`}
                        />
                      </td>
                      {/* HSN - Read only */}
                      <td className="px-2 py-1.5">
                        <span className="text-xs text-gray-500">{item.hsn_code || '-'}</span>
                      </td>
                      {/* Available Stock at Source Branch */}
                      <td className="px-2 py-1.5 text-right">
                        {sourceBranch && item.product_id ? (
                          (() => {
                            const avail = stockMap[item.product_id];
                            if (avail === undefined) return <span className="text-[10px] text-gray-300">-</span>;
                            const sufficient = avail >= (item.quantity || 0);
                            return (
                              <span data-testid={`stn-stock-${idx}`} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${sufficient ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                {Number(avail).toFixed(1)} {item.unit}
                              </span>
                            );
                          })()
                        ) : <span className="text-[10px] text-gray-300">-</span>}
                      </td>
                      {/* Quantity */}
                      <td className="px-2 py-1.5">
                        <input data-testid={`stn-item-qty-${idx}`} type="number" min="0.01" step="any" value={item.quantity}
                          onChange={e => updateItem(idx, 'quantity', e.target.value)}
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md text-right focus:outline-none focus:ring-1 focus:ring-blue-200" />
                      </td>
                      {/* Unit Price */}
                      <td className="px-2 py-1.5">
                        <input data-testid={`stn-item-rate-${idx}`} type="number" min="0" step="any" value={item.rate}
                          onChange={e => updateItem(idx, 'rate', e.target.value)}
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md text-right focus:outline-none focus:ring-1 focus:ring-blue-200" />
                      </td>
                      {/* Total Amount */}
                      <td className="px-2 py-1.5 text-right font-semibold text-gray-800 text-xs">
                        INR {calcTotal(item).toLocaleString('en-IN')}
                      </td>
                      <td className="px-2 py-1.5">
                        <button onClick={() => removeItem(idx)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* + Add Row / + Add Signage buttons */}
            <div className="border-t border-gray-100 px-3 py-2 flex items-center gap-4">
              <button onClick={addEmptyRow} data-testid="stn-add-row-btn"
                className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800">
                <Plus className="w-3.5 h-3.5" /> Add Row
              </button>
              <button onClick={() => { setSignageForm(emptySignage); setShowSignageModal(true); }} data-testid="stn-add-signage-btn"
                className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
                <Plus className="w-3.5 h-3.5" /> Add Signage
              </button>
            </div>

            <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 flex justify-end">
              <div className="space-y-1 text-xs w-64">
                <div className="flex justify-between"><span className="text-gray-500">Total Amount:</span><span className="font-semibold text-gray-700">INR {subtotal.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">GST:</span><span className="font-semibold text-gray-700">INR {totalGst.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between border-t border-gray-200 pt-1"><span className="font-bold text-gray-800">Final Total:</span><span className="font-bold text-gray-900" data-testid="stn-grand-total">INR {grandTotal.toLocaleString('en-IN')}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Remarks */}
        <div>
          <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Remarks</label>
          <textarea data-testid="stn-remarks" value={remarks} onChange={e => setRemarks(e.target.value)}
            placeholder="Any additional notes..."
            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 min-h-[50px]" />
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onBack} className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="stn-cancel-btn">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting}
            data-testid="stn-submit-btn"
            className="flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Save as Draft
          </button>
        </div>
      </div>

      {/* Add Signage modal */}
      {showSignageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="stn-signage-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Add New Signage</h3>
              <button onClick={() => setShowSignageModal(false)} className="p-1 rounded hover:bg-gray-100" data-testid="stn-signage-close">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-[11px] text-gray-500 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                Define a signage board. A Finished-Goods product & BOM are created, and on confirming the STN a production order is auto-created. Your team adds raw materials on the production order before producing.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Shape *</label>
                  <select data-testid="stn-signage-shape" value={signageForm.shape}
                    onChange={e => setSignageForm(f => ({ ...f, shape: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-200">
                    {['Rectangular', 'Gantry', 'Hazard', 'Circular', 'Octagon', 'Triangular'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Quantity *</label>
                  <input data-testid="stn-signage-qty" type="number" min="1" step="1" value={signageForm.quantity}
                    onChange={e => setSignageForm(f => ({ ...f, quantity: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-200" />
                </div>
              </div>
              {lhShapes.includes(signageForm.shape) ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Length (mm) *</label>
                    <input data-testid="stn-signage-length" type="number" min="0" step="any" value={signageForm.length_mm}
                      onChange={e => setSignageForm(f => ({ ...f, length_mm: e.target.value }))}
                      className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-200" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Height / Breadth (mm) *</label>
                    <input data-testid="stn-signage-height" type="number" min="0" step="any" value={signageForm.height_mm}
                      onChange={e => setSignageForm(f => ({ ...f, height_mm: e.target.value }))}
                      className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-200" />
                  </div>
                </div>
              ) : roundShapes.includes(signageForm.shape) ? (
                <div>
                  <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Diameter (mm) *</label>
                  <input data-testid="stn-signage-diameter" type="number" min="0" step="any" value={signageForm.diameter_mm}
                    onChange={e => setSignageForm(f => ({ ...f, diameter_mm: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-200" />
                </div>
              ) : (
                <div>
                  <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Dimensions *</label>
                  <input data-testid="stn-signage-dimensions" value={signageForm.dimensions}
                    onChange={e => setSignageForm(f => ({ ...f, dimensions: e.target.value }))}
                    placeholder="e.g. 900mm side"
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-200" />
                </div>
              )}
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Material</label>
                <input data-testid="stn-signage-material" value={signageForm.base_material}
                  onChange={e => setSignageForm(f => ({ ...f, base_material: e.target.value }))}
                  placeholder="e.g. ACP / Aluminium"
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-200" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Description</label>
                <textarea data-testid="stn-signage-description" value={signageForm.description}
                  onChange={e => setSignageForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Any additional details..."
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-200 min-h-[50px]" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100">
              <button onClick={() => setShowSignageModal(false)} className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="stn-signage-cancel">Cancel</button>
              <button onClick={handleAddSignage} disabled={savingSignage} data-testid="stn-signage-add"
                className="flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#4f46e5' }}>
                {savingSignage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add to Transfer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DETAIL VIEW ====================

const STNDetailView = ({ stnId, onBack, branches: branchesFromParent }) => {
  const [stn, setStn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');

  // Edit mode for Draft STN
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editRemarks, setEditRemarks] = useState('');
  const [editShipTo, setEditShipTo] = useState('');
  const [saving, setSaving] = useState(false);

  // Vehicle dialog (Confirmed -> Ready to Dispatch)
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleNotes, setVehicleNotes] = useState('');

  // Invoice dialog
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [invoiceDocName, setInvoiceDocName] = useState('');
  const [invoiceUploading, setInvoiceUploading] = useState(false);

  // Confirm dispatch loading
  const [confirmDispatchLoading, setConfirmDispatchLoading] = useState(false);

  // Cancel STN dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Edit Vehicle Number dialog (RTD + no invoice only)
  const [showEditVehicleDialog, setShowEditVehicleDialog] = useState(false);
  const [editVehicleValue, setEditVehicleValue] = useState('');
  const [savingVehicle, setSavingVehicle] = useState(false);

  const handleSaveVehicleNumber = async () => {
    const newVeh = editVehicleValue.trim().toUpperCase();
    if (!newVeh) {
      toast.error('Vehicle number is required');
      return;
    }
    if (newVeh === (stn?.vehicle_no || '').toUpperCase()) {
      toast.error('Vehicle number unchanged');
      return;
    }
    try {
      setSavingVehicle(true);
      await stockTransferAPI.updateVehicleNumber(stnId, newVeh);
      toast.success(`Vehicle number updated to ${newVeh}`);
      setShowEditVehicleDialog(false);
      await fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update vehicle number');
    } finally {
      setSavingVehicle(false);
    }
  };

  const handleCancelSTN = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    if (!window.confirm(`Cancel STN ${stn?.stn_number}? This will reverse any stock movements and cancel linked dispatches & production orders. This cannot be undone.`)) return;
    try {
      setCancelling(true);
      const result = await stockTransferAPI.cancel(stnId, cancelReason);
      toast.success(`STN cancelled${(result.actions || []).length ? ` — ${result.actions.join(', ')}` : ''}`);
      setShowCancelDialog(false);
      setCancelReason('');
      await fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel STN');
    } finally {
      setCancelling(false);
    }
  };

  const handleAmendSave = async () => {
    try {
      setSaving(true);
      await stockTransferAPI.amend(stnId, {
        items: editItems,
        ship_to: editShipTo,
        notes: editRemarks,
      });
      toast.success('STN amended successfully');
      setEditing(false);
      await fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to amend STN');
    } finally {
      setSaving(false);
    }
  };

  const fetchSTN = useCallback(async () => {
    try {
      setLoading(true);
      const data = await stockTransferAPI.getById(stnId);
      setStn(data);
    } catch (err) {
      toast.error('Failed to load STN');
    } finally {
      setLoading(false);
    }
  }, [stnId]);

  useEffect(() => { fetchSTN(); }, [fetchSTN]);

  const startEditing = () => {
    setEditItems((stn.items || []).map(i => ({ ...i })));
    setEditRemarks(stn.remarks || '');
    setEditShipTo(stn.ship_to || '');
    setEditing(true);
  };

  const addEditItem = (product) => {
    if (!product) return;
    if (editItems.some(i => i.product_id === product.id)) {
      toast.error('Item already added');
      return;
    }
    const qty = 1;
    const rate = product.selling_price || 0;
    const gstPct = product.gst_rate || 18;
    const amount = round2(qty * rate);
    const gstAmt = round2(amount * (gstPct / 100));
    setEditItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku || '',
      hsn_code: product.hsn_code || '',
      quantity: qty,
      unit: product.unit || 'PCS',
      rate,
      gst_percent: gstPct,
      amount,
      gst_amount: gstAmt,
      total_amount: round2(amount + gstAmt),
    }]);
  };

  const updateEditItem = (idx, field, val) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: field === 'quantity' || field === 'rate' || field === 'gst_percent' ? (parseFloat(val) || 0) : val };
      if (field === 'quantity' || field === 'rate' || field === 'gst_percent') {
        const amount = round2((updated.quantity || 0) * (updated.rate || 0));
        const gstAmt = round2(amount * ((updated.gst_percent || 18) / 100));
        updated.amount = amount;
        updated.gst_amount = gstAmt;
        updated.total_amount = round2(amount + gstAmt);
      }
      return updated;
    }));
  };

  const saveEdits = async () => {
    try {
      setSaving(true);
      await stockTransferAPI.update(stnId, {
        items: editItems,
        remarks: editRemarks,
        ship_to: editShipTo,
      });
      toast.success('STN updated successfully');
      setEditing(false);
      fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!window.confirm('Confirm this STN? Stock will be checked and production orders created for any shortfall.')) return;
    try {
      setActionLoading('confirm');
      const result = await stockTransferAPI.confirm(stnId);
      if (result.shortfall_items?.length > 0) {
        toast.success(`Confirmed. ${result.production_orders_created} production order(s) created for shortfall.`, { duration: 5000 });
      } else {
        toast.success('Confirmed. All stock available.');
      }
      fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    } finally {
      setActionLoading('');
    }
  };

  const handleReadyToDispatch = async () => {
    if (!vehicleNumber.trim()) { toast.error('Vehicle number is required'); return; }
    try {
      setActionLoading('vehicle');
      await stockTransferAPI.readyToDispatch(stnId, {
        vehicle_number: vehicleNumber.trim().toUpperCase(),
        notes: vehicleNotes,
      });
      toast.success('Moved to Ready to Dispatch. Dispatch note created.');
      setShowVehicleDialog(false);
      fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleInvoiceFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setInvoiceUploading(true);
      const res = await fileAPI.uploadDocument(file);
      setInvoiceUrl(res.url || res.file_path);
      setInvoiceDocName(file.name);
      toast.success('Invoice file uploaded');
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setInvoiceUploading(false);
    }
  };

  const handleInvoiceSubmit = async () => {
    if (!invoiceNumber) { toast.error('Invoice number is required'); return; }
    try {
      setActionLoading('invoice');
      await stockTransferAPI.uploadInvoice(stnId, {
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        invoice_url: invoiceUrl,
        invoice_doc_name: invoiceDocName,
      });
      toast.success('Invoice saved successfully');
      setShowInvoiceDialog(false);
      fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setActionLoading('');
    }
  };

  const handleConfirmDispatch = async () => {
    if (!window.confirm('Confirm dispatch? Stock will be deducted from source branch and dispatch will be activated.')) return;
    try {
      setConfirmDispatchLoading(true);
      await stockTransferAPI.confirmDispatch(stnId);
      toast.success('Dispatch confirmed! Stock deducted from source branch.');
      fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dispatch confirmation failed');
    } finally {
      setConfirmDispatchLoading(false);
    }
  };

  const handleReceive = async () => {
    if (!window.confirm('Confirm receipt? This will add stock to the destination branch.')) return;
    try {
      setActionLoading('receive');
      await stockTransferAPI.receive(stnId);
      toast.success('Receipt confirmed! Stock added to destination branch.');
      fetchSTN();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Receipt confirmation failed');
    } finally {
      setActionLoading('');
    }
  };

  const downloadPDF = () => {
    if (!stn) return;
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('STOCK TRANSFER NOTE', 105, 20, { align: 'center' });
    doc.setFontSize(9);
    doc.text('SHARDA INFRASOLUTIONS PVT. LTD.', 105, 28, { align: 'center' });
    doc.setDrawColor(36, 84, 156);
    doc.setLineWidth(0.5);
    doc.line(15, 32, 195, 32);

    let y = 42;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const addRow = (label, value) => { doc.setFont('helvetica', 'bold'); doc.text(label, 15, y); doc.setFont('helvetica', 'normal'); doc.text(String(value || '-'), 70, y); y += 6; };

    addRow('STN #:', stn.stn_number);
    addRow('Status:', stn.status);
    addRow('Date:', stn.created_at ? new Date(stn.created_at).toLocaleDateString('en-IN') : '-');
    addRow('From:', stn.source_branch_name);
    addRow('To:', stn.destination_branch_name);
    if (stn.ship_to) addRow('Ship To:', stn.ship_to);
    if (stn.vehicle_no) addRow('Vehicle:', stn.vehicle_no);
    if (stn.transporter_name) addRow('Transporter:', stn.transporter_name);
    if (stn.lr_number) addRow('LR Number:', stn.lr_number);
    if (stn.invoice_number) addRow('Invoice #:', stn.invoice_number);
    if (stn.invoice_date) addRow('Invoice Date:', stn.invoice_date);

    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('ITEMS', 15, y); y += 4;
    doc.line(15, y, 195, y); y += 5;

    doc.setFontSize(8);
    doc.text('#', 18, y); doc.text('Product', 30, y); doc.text('Qty', 110, y); doc.text('Rate', 130, y); doc.text('Amount', 155, y); doc.text('Total', 178, y);
    y += 2; doc.line(15, y, 195, y); y += 5;

    doc.setFont('helvetica', 'normal');
    (stn.items || []).forEach((item, i) => {
      doc.text(String(i + 1), 18, y);
      doc.text(item.product_name || '', 30, y);
      doc.text(`${item.quantity} ${item.unit}`, 110, y);
      doc.text(String(item.rate || 0), 130, y);
      doc.text(String(item.amount || 0), 155, y);
      doc.text(String(item.total_amount || 0), 178, y);
      y += 5;
    });

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.text(`Subtotal: INR ${(stn.subtotal || 0).toLocaleString()}`, 140, y); y += 5;
    doc.text(`GST: INR ${(stn.total_gst || 0).toLocaleString()}`, 140, y); y += 5;
    doc.text(`Grand Total: INR ${(stn.grand_total || 0).toLocaleString()}`, 140, y);

    if (stn.remarks) { y += 8; doc.setFont('helvetica', 'bold'); doc.text('Remarks:', 15, y); y += 5; doc.setFont('helvetica', 'normal'); doc.text(stn.remarks, 15, y); }

    doc.save(`${stn.stn_number}.pdf`);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!stn) return <div className="text-center py-16 text-gray-400">STN not found</div>;

  const isAdmin = user.role === 'Admin' || user.role === 'Manager';
  const hasInvoice = !!stn.invoice_number;
  const showConfirmDispatchBtn = stn.status === 'Ready to Dispatch' && hasInvoice;

  return (
    <div data-testid="stn-detail-view">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-4" data-testid="stn-detail-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Stock Transfers
      </button>

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }} data-testid="stn-detail-number">{stn.stn_number}</h2>
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${statusColors[stn.status] || 'bg-gray-100 text-gray-600'}`} data-testid="stn-detail-status">{stn.status}</span>
          </div>
          <p className="text-[11px] text-gray-500">Created by {stn.created_by_name} on {stn.created_at ? new Date(stn.created_at).toLocaleDateString('en-IN') : '-'}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Draft: Edit + Confirm */}
          {stn.status === 'Draft' && !editing && (
            <>
              <button data-testid="stn-edit-btn" onClick={startEditing} disabled={!!actionLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50">
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
              <button data-testid="stn-confirm-btn" onClick={handleConfirm} disabled={!!actionLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-green-600 hover:bg-green-700 disabled:opacity-50">
                {actionLoading === 'confirm' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirm STN
              </button>
            </>
          )}
          {stn.status === 'Draft' && editing && (
            <>
              <button onClick={() => setEditing(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button data-testid="stn-save-edit-btn" onClick={saveEdits} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#059669' }}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save Changes
              </button>
            </>
          )}
          {/* Confirmed: Vehicle -> Ready to Dispatch */}
          {stn.status === 'Confirmed' && (
            <button data-testid="stn-ready-dispatch-btn" onClick={async () => {
              // Run stock check before opening vehicle dialog — block if source stock insufficient (HQ + non-HQ alike)
              try {
                setActionLoading('stock-check');
                toast.info('Checking stock availability at source...');
                const result = await stockTransferAPI.stockCheck(stnId);
                if (!result.all_sufficient) {
                  const shortItems = (result.items || []).filter(i => !i.sufficient);
                  const details = shortItems.map(i => `${i.product_name}: need ${i.required_qty}, available ${i.available_qty} ${i.unit}`).join('\n');
                  toast.error(`Cannot dispatch — insufficient stock at ${result.source_branch_name || 'source'}`, { duration: 6000 });
                  alert(`Insufficient stock at ${result.source_branch_name || 'source branch'}:\n\n${details}\n\nPlease arrange stock (via production, inward, or branch transfer) before dispatching.`);
                  return;
                }
              } catch (err) {
                console.error('Stock check failed:', err);
                toast.error(err.response?.data?.detail || 'Stock check failed');
                return;
              } finally {
                setActionLoading('');
              }
              setShowVehicleDialog(true);
            }} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50">
              {actionLoading === 'stock-check' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />} Ready to Dispatch
            </button>
          )}
          {/* Ready to Dispatch: Invoice upload */}
          {stn.status === 'Ready to Dispatch' && (
            <button data-testid="stn-upload-invoice-btn" onClick={() => {
              setInvoiceNumber(stn.invoice_number || '');
              setInvoiceDate(stn.invoice_date || '');
              setInvoiceUrl(stn.invoice_url || '');
              setInvoiceDocName(stn.invoice_doc_name || '');
              setShowInvoiceDialog(true);
            }} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-[#24549c] hover:bg-[#1a3f7a] disabled:opacity-50">
              <FileText className="w-3.5 h-3.5" /> {hasInvoice ? 'Edit Invoice' : 'Add Invoice'}
            </button>
          )}
          {/* Ready to Dispatch + Invoice: Confirm Dispatch */}
          {showConfirmDispatchBtn && (
            <button data-testid="stn-confirm-dispatch-btn" onClick={handleConfirmDispatch} disabled={confirmDispatchLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              title="Confirm dispatch - mark as Dispatched">
              {confirmDispatchLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-4 h-4" />}
              Confirm Dispatch
            </button>
          )}
          {/* Dispatched: Confirm Receipt */}
          {stn.status === 'Dispatched' && isAdmin && (
            <button onClick={handleReceive} disabled={!!actionLoading}
              data-testid="stn-receive-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
              {actionLoading === 'receive' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
              Confirm Receipt
            </button>
          )}
          {/* Amend STN (Admin/Manager) - Confirmed / Ready to Dispatch states */}
          {isAdmin && !editing && ['Confirmed', 'Ready to Dispatch'].includes(stn.status) && (
            <button
              data-testid="stn-amend-btn"
              onClick={startEditing}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50"
              title="Amend STN — change items / ship-to (before Dispatched only)"
            >
              <Edit3 className="w-3.5 h-3.5" /> Amend
            </button>
          )}
          {isAdmin && editing && ['Confirmed', 'Ready to Dispatch'].includes(stn.status) && (
            <>
              <button onClick={() => setEditing(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button
                data-testid="stn-save-amend-btn"
                onClick={handleAmendSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save Amendment
              </button>
            </>
          )}
          {/* Cancel STN (Admin/Manager) - any non-Cancelled stage */}
          {isAdmin && stn.status && stn.status !== 'Cancelled' && !editing && (
            <button
              data-testid="stn-cancel-stn-btn"
              onClick={() => { setCancelReason(''); setShowCancelDialog(true); }}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
              title="Cancel this STN (reverses stock movements and linked dispatches)"
            >
              <X className="w-3.5 h-3.5" /> Cancel STN
            </button>
          )}
          {/* Download & Print */}
          <button onClick={downloadPDF} data-testid="stn-download-pdf-btn"
            className="p-1.5 rounded-lg hover:bg-gray-100" title="Download PDF"><Download className="w-3.5 h-3.5 text-gray-500" /></button>
          <button onClick={() => { downloadPDF(); window.print(); }}
            className="p-1.5 rounded-lg hover:bg-gray-100" title="Print"><Printer className="w-3.5 h-3.5 text-gray-500" /></button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-[10px] font-semibold text-gray-400 mb-1">FROM (SOURCE)</p>
          <p className="text-sm font-semibold text-gray-800" data-testid="stn-detail-source">{stn.source_branch_name}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-[10px] font-semibold text-gray-400 mb-1">TO (DESTINATION)</p>
          <p className="text-sm font-semibold text-gray-800" data-testid="stn-detail-dest">{stn.destination_branch_name}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-[10px] font-semibold text-gray-400 mb-1">SHIP TO</p>
          <p className="text-xs text-gray-600">{stn.ship_to || '-'}</p>
        </div>
      </div>

      {/* OC-style info panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="lg:col-span-2">
          {/* Items Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-5">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-xs font-bold text-gray-700">Transfer Items ({(editing ? editItems : stn.items || []).length})</h3>
            </div>
            {editing ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-amber-50/30 border-b border-gray-100">
                      <th className="text-left px-3 py-2 font-semibold text-gray-500">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-500">Product</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500 w-20">Qty</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-500 w-14">Unit</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500 w-24">Rate</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500 w-14">GST%</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500 w-24">Total</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {editItems.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-50">
                        <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                        <td className="px-3 py-2 font-medium text-gray-800">{item.product_name} <span className="text-[10px] text-gray-400 font-mono">{item.sku}</span></td>
                        <td className="px-3 py-2">
                          <input type="number" step="any" min="0.01" value={item.quantity} onChange={e => updateEditItem(idx, 'quantity', e.target.value)}
                            data-testid={`stn-edit-qty-${idx}`}
                            className="w-full px-2 py-1 text-xs border border-amber-200 rounded text-right bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-300" />
                        </td>
                        <td className="px-3 py-2 text-gray-500">{item.unit}</td>
                        <td className="px-3 py-2">
                          <input type="number" step="any" min="0" value={item.rate} onChange={e => updateEditItem(idx, 'rate', e.target.value)}
                            data-testid={`stn-edit-rate-${idx}`}
                            className="w-full px-2 py-1 text-xs border border-amber-200 rounded text-right bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-300" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="any" min="0" value={item.gst_percent} onChange={e => updateEditItem(idx, 'gst_percent', e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-200" />
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-800">INR {(item.total_amount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2">
                          {editItems.length > 1 && (
                            <button onClick={() => setEditItems(prev => prev.filter((_, i) => i !== idx))} className="p-0.5 rounded hover:bg-red-50">
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Add Item Row */}
                <div className="border-t border-dashed border-amber-200 bg-amber-50/30 px-4 py-3 flex items-center gap-3" data-testid="stn-edit-add-item">
                  <Plus className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1 max-w-xs" style={{ overflow: 'visible', position: 'relative', zIndex: 20 }}>
                    <ProductSearchInput
                      value=""
                      onSelect={(product) => addEditItem(product)}
                      onChange={() => {}}
                      onClear={() => {}}
                      placeholder="Search & add product..."
                      testId="stn-edit-add-product-search"
                    />
                  </div>
                  <span className="text-[10px] text-amber-600">Search by name or SKU to add items</span>
                </div>
                <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 flex justify-end">
                  <div className="space-y-1 text-xs w-64">
                    <div className="flex justify-between"><span className="text-gray-500">Subtotal:</span><span className="font-semibold">INR {editItems.reduce((s, i) => s + (i.amount || 0), 0).toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">GST:</span><span className="font-semibold">INR {editItems.reduce((s, i) => s + (i.gst_amount || 0), 0).toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between border-t border-gray-200 pt-1"><span className="font-bold">Grand Total:</span><span className="font-bold">INR {editItems.reduce((s, i) => s + (i.total_amount || 0), 0).toLocaleString('en-IN')}</span></div>
                  </div>
                </div>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="text-left px-4 py-2 font-semibold text-gray-500">#</th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-500">Item ID</th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-500">Product</th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-500">Qty</th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-500">Unit</th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-500">Rate</th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-500">Amount</th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-500">GST%</th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-500">GST</th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-500">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(stn.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-50">
                      <td className="px-4 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-4 py-2 text-gray-500 font-mono text-[10px]">{item.sku}</td>
                      <td className="px-4 py-2 font-medium text-gray-800">{item.product_name}</td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-800">{item.quantity?.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-2 text-gray-500">{item.unit}</td>
                      <td className="px-4 py-2 text-right text-gray-700">INR {(item.rate || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-2 text-right text-gray-700">INR {(item.amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{item.gst_percent || 0}%</td>
                      <td className="px-4 py-2 text-right text-gray-500">INR {(item.gst_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-800">INR {(item.total_amount || 0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            {/* Totals */}
            {!editing && (
            <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 flex justify-end">
              <div className="space-y-1 text-xs w-64">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal:</span><span className="font-semibold">INR {(stn.subtotal || 0).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Total GST:</span><span className="font-semibold">INR {(stn.total_gst || 0).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between border-t border-gray-200 pt-1"><span className="font-bold text-gray-800">Grand Total:</span><span className="font-bold text-gray-900" data-testid="stn-detail-grand-total">INR {(stn.grand_total || 0).toLocaleString('en-IN')}</span></div>
              </div>
            </div>
            )}
          </div>

          {/* Remarks - Editable in edit mode */}
          {editing ? (
            <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5">
              <h3 className="text-xs font-bold text-gray-700 mb-1">Remarks</h3>
              <textarea value={editRemarks} onChange={e => setEditRemarks(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 min-h-[50px]" placeholder="Remarks..." />
            </div>
          ) : stn.remarks ? (
            <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5">
              <h3 className="text-xs font-bold text-gray-700 mb-1">Remarks</h3>
              <p className="text-xs text-gray-600">{stn.remarks}</p>
            </div>
          ) : null}
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Branch (From)</span><span className="font-semibold">{stn.source_branch_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Branch (To)</span><span className="font-semibold">{stn.destination_branch_name}</span></div>
              {stn.vehicle_no && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Vehicle</span>
                  <span className="flex items-center gap-1.5">
                    <span className="font-semibold text-amber-700" data-testid="stn-vehicle-display">{stn.vehicle_no}</span>
                    {isAdmin && stn.status === 'Ready to Dispatch' && !stn.invoice_number && (
                      <button
                        type="button"
                        data-testid="stn-edit-vehicle-btn"
                        onClick={() => { setEditVehicleValue(stn.vehicle_no || ''); setShowEditVehicleDialog(true); }}
                        title="Edit vehicle number (available until invoice is uploaded)"
                        className="p-0.5 rounded hover:bg-amber-50 text-amber-600 hover:text-amber-800"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                </div>
              )}
              {stn.latest_dispatch_number && <div className="flex justify-between"><span className="text-gray-500">Dispatch Note</span><span className="font-mono font-semibold text-emerald-700">{stn.latest_dispatch_number}</span></div>}
              {stn.invoice_number && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Invoice</span>
                  {stn.invoice_url ? (
                    <a href={`${API_BASE}/api/files/view?path=${encodeURIComponent(stn.invoice_url)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="font-semibold text-emerald-700 hover:underline flex items-center gap-1" data-testid="stn-invoice-link">
                      {stn.invoice_number}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="font-semibold text-emerald-700">{stn.invoice_number}</span>
                  )}
                </div>
              )}
              {stn.invoice_date && <div className="flex justify-between"><span className="text-gray-500">Invoice Date</span><span>{stn.invoice_date}</span></div>}
              {stn.transporter_name && <div className="flex justify-between"><span className="text-gray-500">Transporter</span><span className="font-semibold">{stn.transporter_name}</span></div>}
              {stn.lr_number && <div className="flex justify-between"><span className="text-gray-500">LR Number</span><span className="font-semibold">{stn.lr_number}</span></div>}
              {stn.dispatch_date && <div className="flex justify-between"><span className="text-gray-500">Dispatch Date</span><span className="font-semibold">{stn.dispatch_date}</span></div>}
            </div>

            {/* Receipt Info */}
            {stn.received_at && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Receipt Confirmation</h4>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Received By</span><span className="font-semibold">{stn.received_by_name}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="font-semibold">{new Date(stn.received_at).toLocaleDateString('en-IN')}</span></div>
                </div>
              </div>
            )}

            {/* Status History */}
            {(stn.status_history || []).length > 0 && (
              <div className="mt-4">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Status History</h4>
                <div className="space-y-2">
                  {(stn.status_history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-[10px] font-semibold text-gray-700">{h.action}</p>
                        <p className="text-[9px] text-gray-400">{h.user} | {h.timestamp ? new Date(h.timestamp).toLocaleString('en-IN') : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vehicle Dialog (Confirmed -> Ready to Dispatch) */}
      {showVehicleDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="stn-vehicle-modal-overlay">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-5" data-testid="stn-vehicle-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-bold text-gray-900">Dispatch Note - {stn.stn_number}</h3>
              </div>
              <button onClick={() => setShowVehicleDialog(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>

            {/* Vehicle Number */}
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Vehicle Number <span className="text-red-500">*</span></label>
              <input data-testid="stn-vehicle-number-input" value={vehicleNumber}
                onChange={e => setVehicleNumber(e.target.value.toUpperCase())}
                placeholder="e.g. MH02AB1234"
                className="w-full px-3 py-2.5 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Notes (optional)</label>
              <textarea rows={2} value={vehicleNotes} onChange={e => setVehicleNotes(e.target.value)}
                placeholder="Any special instructions..."
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" data-testid="stn-vehicle-notes" />
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowVehicleDialog(false)} className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleReadyToDispatch} disabled={actionLoading === 'vehicle' || !vehicleNumber.trim()}
                data-testid="stn-confirm-vehicle-btn"
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50">
                {actionLoading === 'vehicle' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                Create Dispatch Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Upload Dialog */}
      {showInvoiceDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="stn-invoice-modal-overlay">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-5" data-testid="stn-invoice-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" style={{ color: '#24549c' }} />
                <h3 className="text-sm font-bold text-gray-900">Stock Transfer Invoice - {stn.stn_number}</h3>
              </div>
              <button onClick={() => setShowInvoiceDialog(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg text-xs">
                <div className="flex justify-between"><span className="text-gray-500">From</span><span className="font-semibold">{stn.source_branch_name}</span></div>
                <div className="flex justify-between mt-1"><span className="text-gray-500">To</span><span className="font-semibold">{stn.destination_branch_name}</span></div>
                {stn.vehicle_no && <div className="flex justify-between mt-1"><span className="text-gray-500">Vehicle</span><span className="font-semibold text-amber-700">{stn.vehicle_no}</span></div>}
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Invoice Number <span className="text-red-500">*</span></label>
                <input data-testid="stn-invoice-number" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)}
                  placeholder="e.g., INV-2026-001"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Invoice Date</label>
                <input data-testid="stn-invoice-date" type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Upload Invoice Document <span className="text-red-500">*</span></label>
                <input type="file" accept=".pdf,.jpg,.png,.jpeg,.doc,.docx,.xls,.xlsx" onChange={handleInvoiceFileUpload}
                  data-testid="stn-invoice-file"
                  className="w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                {invoiceDocName && <p className="text-[10px] text-green-600 mt-1">Uploaded: {invoiceDocName}</p>}
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowInvoiceDialog(false)} className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleInvoiceSubmit} disabled={actionLoading === 'invoice' || invoiceUploading || !invoiceNumber.trim()}
                data-testid="stn-invoice-submit-btn"
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-[#24549c] hover:bg-[#1a3f7a] disabled:opacity-50">
                {actionLoading === 'invoice' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                Save Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vehicle Number Dialog (RTD + no invoice only) */}
      {showEditVehicleDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="stn-edit-vehicle-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <Truck className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Edit Vehicle Number</h3>
            </div>
            <p className="text-[11px] text-gray-600 mb-3">
              Change the vehicle number for <span className="font-semibold">{stn?.stn_number}</span>. This will also update the linked dispatch ({stn?.latest_dispatch_number || 'pending'}).
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
              <p className="text-[10px] text-blue-900">
                💡 Vehicle number is editable only until the invoice is uploaded. Once uploaded, the number on the Tally invoice and ERP must match.
              </p>
            </div>
            <label className="text-[11px] font-semibold text-gray-600 mb-1 block">New Vehicle Number <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={editVehicleValue}
              onChange={e => setEditVehicleValue(e.target.value.toUpperCase())}
              placeholder="e.g. JH01XY9999"
              data-testid="stn-edit-vehicle-input"
              maxLength={20}
              className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg uppercase font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <p className="text-[10px] text-gray-500 mt-1">Current: <span className="font-mono">{stn?.vehicle_no || '-'}</span></p>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => setShowEditVehicleDialog(false)}
                disabled={savingVehicle}
                className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="stn-edit-vehicle-save-btn"
                onClick={handleSaveVehicleNumber}
                disabled={savingVehicle || !editVehicleValue.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {savingVehicle ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Update Vehicle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel STN Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="stn-cancel-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-md">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <X className="w-4 h-4 text-red-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Cancel Stock Transfer</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              You're about to cancel <span className="font-semibold">{stn?.stn_number}</span> (status: <span className="font-semibold">{stn?.status}</span>).
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3">
              <p className="text-[10px] text-amber-900 font-semibold mb-1">This will automatically:</p>
              <ul className="text-[10px] text-amber-800 list-disc list-inside space-y-0.5">
                {stn?.status === 'Received' && <li>Reverse destination stock entries (delete added qty)</li>}
                {['Ready to Dispatch', 'Dispatched', 'Received'].includes(stn?.status) && <li>Cancel linked dispatch (un-deduct source if applicable)</li>}
                {['Confirmed', 'Ready to Dispatch', 'Dispatched', 'Received'].includes(stn?.status) && <li>Cancel any auto-created production orders</li>}
                <li>Mark STN as Cancelled — this is irreversible</li>
              </ul>
            </div>
            <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Reason for cancellation <span className="text-red-500">*</span></label>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              placeholder="e.g. Wrong destination branch selected, customer order cancelled, duplicate STN..."
              data-testid="stn-cancel-reason"
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => { setShowCancelDialog(false); setCancelReason(''); }}
                disabled={cancelling}
                className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Keep STN
              </button>
              <button
                data-testid="stn-cancel-confirm-btn"
                onClick={handleCancelSTN}
                disabled={cancelling || !cancelReason.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function round2(n) { return Math.round(n * 100) / 100; }

export default StockTransferModule;
